import React from 'react';
import { Brain, Book, Users, Heart, Clock, Sparkles, ArrowRight } from 'lucide-react';
import { programs } from '../data/mock';

const Programs = () => {
  return (
    <div className="min-h-screen bg-[#ede1c5] text-stone-900">
      {/* HERO */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1506126613408-eca07ce68773)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#ede1c5]/85 via-[#ede1c5]/55 to-[#ede1c5]" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-amber-400/12 blur-[140px]" />

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 text-amber-700/90 tracking-[0.3em] text-[11px] uppercase mb-6">
            <span className="h-px w-12 bg-amber-600/60" />
            <Sparkles size={14} />
            <span>Practice · Study · Community</span>
          </div>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.05] text-stone-900 max-w-3xl">
            Our<br /><span className="italic text-amber-800">programs.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-stone-700 leading-relaxed">
            Discover peace through practice — meditation, study, and community gatherings open to all.
          </p>
        </div>
      </section>

      {/* PROGRAMS LIST */}
      <section className="py-24 border-t border-amber-500/40">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 gap-6">
            {programs.map((program) => {
              const Icon = { Brain, Book, Users, Heart }[program.icon];
              return (
                <div
                  key={program.id}
                  className="group p-8 rounded-2xl border border-amber-500/40 bg-[#fbf5e3]/80 hover:bg-[#fbf5e3]/95 hover:border-amber-500/40 transition-all hover:-translate-y-1"
                >
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-300 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20 flex-shrink-0">
                      <Icon className="text-black/80" size={20} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-serif-display text-2xl text-stone-900 mb-3 leading-tight">
                        {program.title}
                      </h3>
                      <p className="text-stone-600 leading-relaxed mb-4">{program.description}</p>
                      {program.schedule && (
                        <div className="flex items-center gap-2 text-amber-800 text-sm tracking-wide">
                          <Clock size={14} />
                          <span>{program.schedule}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MEDITATION */}
      <section className="py-24 bg-[#e3d4af] border-t border-amber-500/40">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-amber-700/80 text-[11px] tracking-[0.3em] uppercase mb-3">
              The Stillness
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-stone-900 mb-6 leading-tight">
              Meditation practice
            </h2>
            <p className="text-stone-700 text-lg leading-relaxed mb-8">
              Our sessions are suitable for everyone — complete beginners to seasoned
              practitioners. We teach both <span className="italic text-amber-800">Samatha</span> (calm
              abiding) and <span className="italic text-amber-800">Vipassanā</span> (insight).
            </p>
            <div className="space-y-5">
              {[
                { n: '01', t: 'Beginner-Friendly', d: 'No prior experience needed. We guide you step by step.' },
                { n: '02', t: 'Guided Sessions', d: 'Led by experienced practitioners with clear instructions.' },
                { n: '03', t: 'Peaceful Environment', d: 'A dedicated space for calm practice and reflection.' },
              ].map((b) => (
                <div key={b.n} className="flex gap-5">
                  <div className="font-serif-display text-3xl text-amber-800/80 leading-none">{b.n}</div>
                  <div>
                    <div className="font-serif-display text-xl text-stone-900 mb-1">{b.t}</div>
                    <div className="text-stone-600 leading-relaxed">{b.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-amber-400/10 blur-3xl rounded-full" />
            <img
              src="https://images.pexels.com/photos/3759657/pexels-photo-3759657.jpeg"
              alt="Meditation practice"
              className="relative rounded-2xl w-full h-[480px] object-cover border border-amber-500/40"
            />
          </div>
        </div>
      </section>

      {/* DHARMA */}
      <section className="py-24 border-t border-amber-500/40">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1 relative">
            <div className="absolute -inset-4 bg-amber-400/10 blur-3xl rounded-full" />
            <img
              src="https://images.pexels.com/photos/14344561/pexels-photo-14344561.jpeg"
              alt="Buddha teaching"
              className="relative rounded-2xl w-full h-[480px] object-cover border border-amber-500/40"
            />
          </div>
          <div className="order-1 lg:order-2">
            <div className="text-amber-700/80 text-[11px] tracking-[0.3em] uppercase mb-3">
              The Wisdom
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-stone-900 mb-6 leading-tight">
              Dhamma teachings
            </h2>
            <p className="text-stone-700 text-lg leading-relaxed mb-6">
              Deepen your understanding of Buddhist philosophy and apply ancient wisdom to modern
              life through our weekly Dhamma talks.
            </p>
            <div className="rounded-2xl border-l-2 border-amber-600/80 bg-[#fbf5e3]/80 p-6 mb-6">
              <div className="text-[10px] tracking-[0.25em] uppercase text-amber-700/80 mb-3">
                Topics include
              </div>
              <ul className="text-stone-700 space-y-2 text-sm">
                <li>· The Four Noble Truths</li>
                <li>· The Noble Eightfold Path</li>
                <li>· Karma and Rebirth</li>
                <li>· Mindfulness in Daily Life</li>
                <li>· Loving-Kindness and Compassion</li>
              </ul>
            </div>
            <p className="text-stone-600 leading-relaxed">
              All teachings presented in clear, accessible English with opportunities for
              questions and discussion.
            </p>
          </div>
        </div>
      </section>

      {/* CHILDREN'S */}
      <section className="py-24 bg-[#e3d4af] border-t border-amber-500/40">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <div className="text-amber-700/80 text-[11px] tracking-[0.3em] uppercase mb-3">
              Sunday Children's Class
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-stone-900">
              Nurturing young hearts
            </h2>
          </div>
          <div className="p-10 rounded-2xl border border-amber-500/40 bg-gradient-to-br from-[#fbf5e3]/95 to-[#f3e7c4]/90">
            <p className="text-stone-700 leading-relaxed mb-8">
              Our Sunday school program is specially designed for children, providing them
              with a supportive environment to learn Buddhist values, meditation basics, and
              cultural traditions.
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-amber-700/80 mb-3">
                  What children learn
                </div>
                <ul className="text-stone-700 space-y-2 text-sm">
                  <li>· Buddhist stories and teachings</li>
                  <li>· Basic meditation techniques</li>
                  <li>· Moral values and ethics</li>
                  <li>· Cultural traditions</li>
                  <li>· Mindfulness practices</li>
                </ul>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-amber-700/80 mb-3">
                  Program features
                </div>
                <ul className="text-stone-700 space-y-2 text-sm">
                  <li>· Age-appropriate activities</li>
                  <li>· Interactive learning</li>
                  <li>· Qualified instructors</li>
                  <li>· Safe, welcoming environment</li>
                  <li>· Cultural celebration events</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-amber-500/40">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif-display text-4xl md:text-5xl text-stone-900 leading-tight mb-5">
            Join us — <span className="italic text-amber-800">all are welcome.</span>
          </h2>
          <p className="text-stone-600 leading-relaxed mb-10">
            All programs are free and open to everyone, regardless of background or experience.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-600 text-black px-8 py-4 rounded-full text-sm tracking-wide"
          >
            Contact Us to Learn More <ArrowRight size={16} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Programs;
